import { t, Trans } from '@lingui/macro'
import * as React from 'react'

import {
    CustomerCoupon,
    useApplyCoupon,
    useLazyGetCustomerCoupons,
} from './customerVouchers.query'
import CheckIcon from '../../../../core/CheckIcon'
import { useLazyGetAppliedCoupons } from '../../../../customer/srsVouchers.query'
import Button, { ButtonProps } from '../../../../input/Button'
import Icon from '../../../../media/Icon'
import { M2ApiResponseError } from '../../../../utils/RestApi'
import withSentry from '../../../../utils/withSentry'

const AddCouponErrors = {
    NO_CART: 'Current customer does not have an active cart.',
    CART_IS_EMPTY: 'The "%1" Cart doesn\'t contain products.',
    INVALID_COUPON_CODE:
        "The coupon code isn't valid. Verify the code and try again.",
}

const renderErrorMessage = (error: string) => {
    switch (error) {
        case AddCouponErrors.NO_CART:
        case AddCouponErrors.CART_IS_EMPTY:
            return t({
                id: 'user.rewards.cartIsEmpty',
                message: `Your cart is empty. Add something to your cart before applying a voucher.`,
            })
        case AddCouponErrors.INVALID_COUPON_CODE:
            return t({
                id: 'user.rewards.invalidCode',
                message: `The coupon code isn't valid. Verify the code and try again.`,
            })
        default:
            return
    }
}

interface Props extends Partial<ButtonProps> {
    voucher: CustomerCoupon
    className?: string
    onApply?(): void
    onApplyError?(message?: string): void
}

const ApplyVoucherButton = ({
    voucher,
    onApply,
    onApplyError,
    ...others
}: Props) => {
    const { getCustomerCoupons } = useLazyGetCustomerCoupons()
    const [isLoading, setLoading] = React.useState<boolean>(false)

    const getAppliedCoupons = useLazyGetAppliedCoupons()

    const applyCoupon = useApplyCoupon()

    const handleClick = async () => {
        setLoading(true)
        try {
            await applyCoupon(voucher.code).then(() => {
                setLoading(false)

                if (onApply) {
                    onApply()
                }
            })
        } catch (err) {
            setLoading(false)
            if (!(err instanceof Error)) {
                return
            }
            let errorMessage = err.message

            if (err instanceof M2ApiResponseError) {
                errorMessage = err.userMessage
            }
            const errorForUser = renderErrorMessage(errorMessage)

            if (errorForUser) {
                onApplyError?.(errorForUser)
            } else {
                withSentry((Sentry) => {
                    Sentry.captureException(err)
                })
            }
            return
        } finally {
            // Fetch customer coupons again so cache gets refreshed
            const { loading } = await getAppliedCoupons()
            setLoading(loading)
            getCustomerCoupons()
        }
    }

    const check = <Icon component={CheckIcon} title="" />

    return (
        <Button
            minWidth
            variant="primary"
            name="Apply voucher"
            category="user.rewards"
            onClick={handleClick}
            loading={isLoading || isLoading}
            disabled={Boolean(voucher.applied) || isLoading}
            {...others}
            className={voucher.applied ? others.className : ''}
        >
            {voucher.isActive ? (
                <Trans id="user.rewards.voucherJustApplied">
                    {check} Applied!
                </Trans>
            ) : voucher.applied ? (
                <Trans id="user.rewards.voucherAlreadyUsed">Already used</Trans>
            ) : (
                <Trans id="user.rewards.applyVoucherButton">Apply</Trans>
            )}
        </Button>
    )
}

export default ApplyVoucherButton
